import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import * as api from "API/api";

const ReviewAppointment = () => {
  const { bookingData, serviceData } = useOutletContext();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setServices(serviceData);
  }, [serviceData]);

  const customer = bookingData?.customer || {};
  const vehicle = bookingData?.vehicle || {};
  const selectedServices = useMemo(() => bookingData?.selectedServices || [], [bookingData?.selectedServices]);
  const selectedDate = bookingData?.selectedDate || "";
  const selectedTime = bookingData?.selectedTime || "";

  const selectedServiceObjects = useMemo(() => {
    return selectedServices
      .map((id) => services.find((s) => s.id === id))
      .filter(Boolean);
  }, [selectedServices, services]);

  // Calculating total service time
  const totalMinutes = selectedServiceObjects.reduce(
    (sum, s) => sum + Number(s.time_taken || 0),
    0
  );

  const canConfirm =
    (customer.fullName || "").trim() &&
    (customer.phone || "").trim() &&
    (customer.email || "").trim() &&
    selectedServices.length > 0 &&
    selectedDate &&
    selectedTime;




  const handleConfirm = async () => {
    if (!canConfirm || submitting) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        name: customer.fullName,
        phone: customer.phone,
        email: customer.email,
        vehicle,
        services: selectedServices,
        date: selectedDate,
        time: `${selectedDate} ${(() => {
          let [t, m] = selectedTime.split(" ");
          let [h, min] = t.split(":");
          h = m === "PM" && h !== "12" ? +h + 12 : m === "AM" && h === "12" ? 0 : +h;
          return `${String(h).padStart(2, "0")}:${min}:00`;
        })()}`,
        totalMinutes,
        status: "scheduled",
      };

      const result = await api.createAppointment(payload);
      navigate("/appointments/bookingConfirmation", {
        state: { apiResult: result },
      });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-8 rounded-2xl bg-white p-6 shadow-sm">
      <h2 className="text-xl font-semibold text-gray-900">Review Your Booking</h2>
      <p className="mt-1 text-sm text-gray-600">
        Please review your appointment details before confirming.
      </p>

      <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
        <div className="grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500">Name</p>
            <p className="mt-1 font-medium text-gray-900">{customer.fullName || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500">Email</p>
            <p className="mt-1 font-medium text-gray-900">{customer.email || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500">Phone</p>
            <p className="mt-1 font-medium text-gray-900">{customer.phone || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500">Vehicle</p>
            <p className="mt-1 font-medium text-gray-900">
              {vehicle.make || vehicle.model || vehicle.year
                ? `${vehicle.make || ""} ${vehicle.model || ""}${vehicle.year ? ` · ${vehicle.year}` : ""}`.trim()
                : "—"}
            </p>
            <p className="mt-1 font-medium text-gray-900">
              {vehicle.plate ? `${vehicle.plate}` : ""}
            </p>
            <p>
              {vehicle.odo ? <p className="mt-1 text-xs text-gray-500"> {vehicle.odo} kms</p> : null}

            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500">Appointment Date & Time</p>
            <p className="mt-1 font-medium text-gray-900">
              {selectedDate && selectedTime ? `${selectedDate} · ${selectedTime}` : "—"}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500">Total Service Time</p>
            <p className="mt-1 font-medium text-gray-900">
              {selectedServiceObjects.length ? `${totalMinutes} mins` : "—"}
            </p>
          </div>
        </div>

        <div className="mt-5 border-t border-gray-200 pt-4">
          <p className="text-xs font-semibold text-gray-500">Selected Services</p>

          {selectedServiceObjects.length === 0 ? (
            <p className="mt-2 text-sm font-medium text-gray-900">—</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {selectedServiceObjects.map((s) => (
                <li
                  key={s.serviceId}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="text-sm font-medium text-gray-900">{s.name}</span>
                  <span className="text-xs font-semibold text-gray-600">{s.time_taken} mins</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>


      {submitError ? (
        <p className="mt-4 text-sm font-semibold text-red-600">{submitError}</p>
      ) : null}

      <div className="mt-8 flex items-center justify-between">
        <Link
          to="/appointments/contactInfo"
          className="rounded-xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition"
        >
          ← Back
        </Link>


        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm || submitting}
          className={`rounded-xl px-6 py-3 text-sm font-semibold text-white transition ${canConfirm && !submitting
              ? "bg-blue-600 hover:bg-blue-500"
              : "bg-gray-300 cursor-not-allowed"
            }`}
        >
          {submitting ? "Confirming..." : "Confirm Booking"}
        </button>
      </div>
    </section>
  );
};

export default ReviewAppointment;
